import { TOTAL_STEPS } from '@/data/onboardingSteps'

/**
 * V7 onboarding progress tracker — replaces the dot-strip ProgressBar.
 *
 * Renders the Illuminate logo with progressive gold-fill bound to current step.
 * Step 1 = ~6% filled (just the bottom edge / one element lit).
 * Step 17 = 100% filled (full logo formed).
 *
 * PLACEHOLDER: until a clean SVG of FinalLogo.png is authored (in-house or Fiverr ~$30),
 * this renders a simple radial-progress fill on a placeholder logo silhouette.
 *
 * Spec: specs/onboarding-rebuild.md → "Progress Tracker (Illuminate Logo Fill)"
 */
export default function OnboardingProgressLogo({ step = 1, totalSteps = TOTAL_STEPS }) {
  const progress = Math.max(0, Math.min(1, step / totalSteps))
  const percent = Math.round(progress * 100)

  // Stroke-dashoffset trick on a circular path for the placeholder.
  // Real implementation: replace <circle> with named-region SVG paths from the logo.
  const radius = 40
  const circumference = 2 * Math.PI * radius
  const offset = circumference * (1 - progress)

  return (
    <div
      className="relative w-20 h-20 mx-auto"
      role="progressbar"
      aria-valuenow={step}
      aria-valuemin={1}
      aria-valuemax={totalSteps}
      aria-label={`Onboarding step ${step} of ${totalSteps}`}
    >
      <svg viewBox="0 0 100 100" className="w-full h-full -rotate-90" aria-hidden="true">
        {/* Background ring (unfilled state) */}
        <circle
          cx="50"
          cy="50"
          r={radius}
          fill="none"
          stroke="var(--bg-surface-high, #1f2937)"
          strokeWidth="6"
        />
        {/* Progress ring (gold fill) */}
        <circle
          cx="50"
          cy="50"
          r={radius}
          fill="none"
          stroke="var(--gold, #d4a017)"
          strokeWidth="6"
          strokeLinecap="round"
          strokeDasharray={circumference}
          strokeDashoffset={offset}
          style={{
            transition: 'stroke-dashoffset 600ms cubic-bezier(0.4, 0, 0.2, 1)',
          }}
        />
      </svg>
      {/* Step counter centered inside the ring */}
      <div className="absolute inset-0 flex items-center justify-center">
        <span className="text-[var(--text-primary)] text-sm font-semibold tabular-nums">
          {step}
          <span className="text-[var(--text-muted)] text-xs">/{totalSteps}</span>
        </span>
      </div>
      <span className="sr-only">{percent}% complete</span>
    </div>
  )
}
